import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Smartphone, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Family memories", "Couples journey", "Proposal", "Birthday hunt",
  "Team building", "Church / youth group", "Travel & road trips", "History & tourism", "Just exploring",
];

export default function EarlyAccess() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Tell us your name, explorer.");
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) {
      return toast.error("Enter a valid mobile number.");
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enroll`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        interest: form.interest || null,
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <main data-testid="enrollment-success" className="pt-16 min-h-[92vh] flex items-center relative overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-[#34C759]/8 blur-3xl pointer-events-none" />
        <div className="relative max-w-xl mx-auto px-5 text-center py-20">
          <div className="relative inline-flex mb-8">
            <span className="absolute inset-0 rounded-full bg-[#34C759]/30 pin-pulse" />
            <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-[#34C759]/15 border border-[#34C759]/40">
              <CheckCircle2 size={34} className="text-[#34C759]" />
            </span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-white">
            You're in.
          </h1>
          <p className="mt-5 text-lg text-[#c9cdd8]">
            {result.status === "already_enrolled"
              ? "You were already enrolled — your spot is safe."
              : <>You're explorer <span className="font-heading font-bold text-[#34C759]">#{result.position}</span> on the map.</>}
          </p>
          <p className="mt-4 text-[#c9cdd8]">
            You're on our text and email list — we'll notify you the moment PinQuests goes live.
          </p>
          <p className="mt-3 text-[#9BA1B0]">
            PinQuests launches first as a website, with iOS and Android apps to follow.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="early-access-page" className="pt-16 min-h-[92vh] relative overflow-hidden grain">
      <div className="absolute left-1/2 -top-40 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#FF453A]/8 blur-3xl pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-5 sm:px-8 py-20 z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-center mb-12">
            <MapPin size={34} className="text-[#FF453A] mx-auto mb-6" />
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white">
              Claim your<br />early access.
            </h1>
            <p className="mt-5 text-[#9BA1B0] text-lg max-w-md mx-auto">
              First explorers get in first. We'll text your invite when the map opens.
            </p>
          </div>

          <form onSubmit={submit} className="flex flex-col gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] mb-2 block">Your name</label>
              <input
                data-testid="enroll-name-input"
                type="text"
                placeholder="What should we call you?"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-2xl bg-[#090A0F] border border-[#2A3042] focus:border-[#FF453A] focus:ring-2 focus:ring-[#FF453A]/30 outline-none text-white placeholder-[#5a6070] px-6 py-4 text-lg transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] mb-2 block flex items-center gap-2">
                <Smartphone size={13} /> Mobile number
              </label>
              <input
                data-testid="enroll-phone-input"
                type="tel"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-2xl bg-[#090A0F] border border-[#2A3042] focus:border-[#FF453A] focus:ring-2 focus:ring-[#FF453A]/30 outline-none text-white placeholder-[#5a6070] px-6 py-5 text-2xl font-heading font-semibold tracking-wide transition-colors"
              />
              <p className="mt-2 text-xs text-[#5a6070]">Your invite arrives by text. We never share your number.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#9BA1B0] mb-2 block">Email <span className="normal-case tracking-normal font-normal">(optional)</span></label>
              <input
                data-testid="enroll-email-input"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-2xl bg-[#090A0F] border border-[#2A3042] focus:border-[#FF453A] focus:ring-2 focus:ring-[#FF453A]/30 outline-none text-white placeholder-[#5a6070] px-6 py-4 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#9BA1B0] mb-3 block">What would you build first?</label>
              <div className="flex flex-wrap gap-2.5">
                {interests.map((it) => (
                  <button
                    key={it}
                    type="button"
                    data-testid={`interest-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setForm({ ...form, interest: form.interest === it ? "" : it })}
                    className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
                      form.interest === it
                        ? "bg-[#FF453A] border-[#FF453A] text-white"
                        : "bg-[#131620] border-[#2A3042] text-[#9BA1B0] hover:border-[#FF453A]/60 hover:text-white"
                    }`}
                  >
                    {it}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid="enrollment-submit-btn"
              type="submit"
              disabled={loading}
              className="mt-4 rounded-full bg-[#FF453A] hover:bg-[#FF6961] disabled:opacity-60 transition-colors text-white font-heading font-bold text-lg py-5 glow-red flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : "Start My Quest"}
            </button>
            <p className="text-center text-xs text-[#5a6070]">
              By enrolling you agree to receive a one-time invite text when PinQuests launches.
            </p>
          </form>
        </motion.div>
      </div>
    </main>
  );
}
